import { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Mobile bottom sheet. Portaled to <body> — an animated ancestor becomes the
 * containing block for position:fixed (learned on the dashboard MemoPicker),
 * so overlays must never render inside the route-transition <main>.
 */
export function Sheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-label={title}>
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-legal-900/50 cursor-default"
      />
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-elevation-3 max-h-[80vh] overflow-y-auto pb-[max(env(safe-area-inset-bottom),0.75rem)] animate-slide-up">
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <h2 className="text-sm font-semibold text-legal-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-2 -mr-2 text-legal-400 hover:text-legal-700 rounded-lg transition-colors press"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-2 pb-2">{children}</div>
      </div>
    </div>,
    document.body
  );
}
